import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Globals } from 'app/services/globals';
import { FTPStorage, Storage, SFTPStorage, DropboxStorage, WebDAVStorage, AmazonS3Storage} from 'app/models/storage';
import { MyFile } from 'app/models/my-file';
import { FormBuilder, Validators } from '@angular/forms';
import { convertObjectArrayToIdArray } from 'app/helpers/utils';
import * as _ from "lodash";
import { BehaviorSubject } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';
import { UserService } from './user.service';
import { Tag } from 'app/models/tag';
import { TagService } from './tag.service';

@Injectable({ providedIn: 'root' })
export class StorageService {
    
    allStoragesSubject: BehaviorSubject<Storage[]> = new BehaviorSubject([]);
    
    tags: Tag[] = [];
    allowedTagsForCreate: Tag[] = [];
    allowedTagsForUpdate: Tag[] = [];
  
    constructor(private http: HttpClient, private globals: Globals, private formBuilder: FormBuilder, private userService: UserService,
                public tagService: TagService) {
        this.userService.currentUser.subscribe(user => {
            if (user && this.globals.api_base_url) {
                this.getAll().subscribe();
                this.tagService.currentTagsSubject.subscribe((t) => { 
                    this.tags = t;
                    this.allowedTagsForCreate = [];
                    this.allowedTagsForUpdate = [];
                    
                    if (this.userService.getPermissions('super.admin.storage.create').all) {
                      this.allowedTagsForCreate = t;
                    } else if (this.userService.getPermissions('super.admin.storage.create').tags) {
                      this.allowedTagsForCreate = _.intersectionBy(t, this.userService.getPermissions('super.admin.storage.create').tags, 'id');
                    }
              
                    if (this.userService.getPermissions('super.admin.storage.create.update').all) {
                      this.allowedTagsForUpdate = t;
                    } else if (this.userService.getPermissions('super.admin.storage.create.update').tags) {
                      this.allowedTagsForUpdate = _.intersectionBy(t, this.userService.getPermissions('super.admin.storage.create.update').tags, 'id');
                    }                                  
                });
            }
        });
     }

    //#region FTP
    addFtp(FTPObject: FTPStorage) {
        return this.add(FTPObject);
    }

    updateFtp(FTPObject: FTPStorage) {
        return this.update(FTPObject);
    }

    ftptestConnection(FTPObject: FTPStorage) {
        return this.testConnection(FTPObject);
    }

    getFTPFormGroup() {
        return this.formBuilder.group({
            id: [undefined],
            display_name: ['', [Validators.required, Validators.maxLength(255)]],
            driver: ['ftp', [Validators.required]],
            path_prefix: [undefined],
            config: this.formBuilder.group({
                host: ['', [Validators.required]],
                username: [undefined],
                password: [undefined],
                ssl: [undefined],
                port: [undefined],
            }),
            tags: [[]]    
          });
    }
    //#endregion

    //#region SFTP
    addSftp(SFTPObject: SFTPStorage) {
        return this.add(SFTPObject);
    }

    updateSftp(SFTPObject: SFTPStorage) {
        return this.update(SFTPObject);
    }

    sftpTestConnection(SFTPObject: SFTPStorage) {
        return this.testConnection(SFTPObject);
    }

    getSFTPFormGroup() {
        return this.formBuilder.group({
            id: [undefined],
            display_name: ['', [Validators.required, Validators.maxLength(255)]],
            driver: ['sftp', [Validators.required]],
            path_prefix: [undefined],
            config: this.formBuilder.group({
                host: ['', [Validators.required]],
                username: [undefined],
                password: [undefined],
                port: [undefined],
            }),
            tags: [[]]    
          });
    }
    //#endregion

    //#region Dropbox
    addDropbox(dropboxObject: DropboxStorage) {
        return this.add(dropboxObject);
    }

    updateDropbox(dropboxObject: DropboxStorage) {
        return this.update(dropboxObject);
    }
    getDropboxFormGroup() {
        return this.formBuilder.group({
            id: [undefined],
            display_name: ['', [Validators.required, Validators.maxLength(255)]],
            driver: ['dropbox', [Validators.required]],
            config: this.formBuilder.group({
                code: [undefined],
                token: [undefined],
            }),
            tags: [[]]    
          });
    }
    //#endregion

    //#region Webdav
    addWebdav(webdavObject: WebDAVStorage) {
        return this.add(webdavObject);
    }

    updateWebdav(webdavObject: WebDAVStorage) {
        return this.update(webdavObject);
    }

    webdavtestConnection(webdavObject: WebDAVStorage) {
        return this.testConnection(webdavObject);
    }

    getWebDAVFormGroup() {
        return this.formBuilder.group({
            id: [undefined],
            display_name: ['', [Validators.required, Validators.maxLength(255)]],
            driver: ['webdav', [Validators.required]],
            path_prefix: [undefined],
            config: this.formBuilder.group({
                baseUri: ['', [Validators.required]],
                username: [undefined],
                password: [undefined],
            }),
            tags: [[]]    
          });
    }
    //#endregion

    //#region Amazons3
    addAmazons3(amazonS3Object: AmazonS3Storage) {
        return this.add(amazonS3Object);
    }

    updateAmazons3(amazonS3Object: AmazonS3Storage) {
        return this.update(amazonS3Object);
    }

    amazons3testConnection(amazonS3Object: AmazonS3Storage) {
        return this.testConnection(amazonS3Object);
    }

    getAmazonS3FormGroup() {
        return this.formBuilder.group({
            id: [undefined],
            display_name: ['', [Validators.required, Validators.maxLength(255)]],
            driver: ['s3', [Validators.required]],
            path_prefix: [undefined],
            config: this.formBuilder.group({
                bucket: [undefined, [Validators.required]],
                key: [undefined, [Validators.required]],
                region: [undefined, [Validators.required]],
                secret: [undefined, [Validators.required]],
                url: [undefined, [Validators.required]],
            }),
            tags: [[]]    
          });
    }
    //#endregion

    //#region Common Storage API 

    getAll(mode: string = "all") {
        return this.http.get<Storage[]>(`${this.globals.api_base_url}/storages?mode=` + mode).pipe(tap((t) => {
            if (mode == 'all')
                this.allStoragesSubject.next(t);
        }));;
    }

    getById(storageId: number) {
        return this.http.get<Storage>(`${this.globals.api_base_url}/storage/${storageId}`);
    }

    add(storage: Storage) {
        const tag_ids = convertObjectArrayToIdArray(storage.tags);
        
        let data = _.cloneDeep(storage);
        data = _.merge(data, {tag_ids: tag_ids});

        return this.http.post<Storage>(`${this.globals.api_base_url}/storage`, data).pipe(finalize(()=> {
            this.getAll().subscribe();  // update the subject
        }));
    }
    update(storage: Storage) {
        const tag_ids = convertObjectArrayToIdArray(storage.tags);
        
        let data = _.cloneDeep(storage);
        data = _.merge(data, {tag_ids: tag_ids});

        return this.http.put<Storage>(`${this.globals.api_base_url}/storage/${storage.id}`, data).pipe(finalize(()=> {
            this.getAll().subscribe();  // update the subject
        }));
    }

    testConnection(storage: Storage) {
        return this.http.post<MyFile[]>(`${this.globals.api_base_url}/storage/test_connection`, storage);
    }


    deactivate(id: number) {
        return this.http.delete(`${this.globals.api_base_url}/storage/${id}`).pipe(finalize(()=> {
            this.getAll().subscribe();  // update the subject
        }));
    }

    restore(id: number) {
        return this.http.post(`${this.globals.api_base_url}/storage/${id}/restore`, {}).pipe(finalize(()=> {
            this.getAll().subscribe();  // update the subject
        }));
    }

    //#endregion

}